import { useState } from 'react';
import { DeckState, Position } from '@/types/game';
import { importDeckFromJson } from '@/utils/deckLoader';

interface JsonDeckImporterProps {
  onImport: (deck: DeckState) => void;
  position: Position;
}

export default function JsonDeckImporter({ onImport, position }: JsonDeckImporterProps) {
  const [deckJson, setDeckJson] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCopyFormat = async () => {
    try {
      await navigator.clipboard.writeText(exampleJson);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleImport = async () => {
    if (!deckJson.trim()) {
      setError('Please enter JSON data');
      return;
    }

    setError(null);
    setIsImporting(true);

    try {
      // Use the shared importDeckFromJson function
      const deck = importDeckFromJson(deckJson, position);
      onImport(deck);
      setDeckJson('');
    } catch (error) {
      console.error("Error importing deck:", error);
      setError(error instanceof Error ? error.message : 'Failed to import deck. Please check the JSON format.');
    } finally {
      setIsImporting(false);
    }
  };

  // Example JSON for the placeholder
  const exampleJson = JSON.stringify({
    tableDeck: {
      name: "My Custom Deck",
      back_image_url: "https://example.com/back.png",
      cards: [
        {
          front_image_url: "https://example.com/card1.png",
          name: "Card 1"
        },
        {
          front_image_url: "https://example.com/card2.png",
          name: "Card 2"
        }
      ]
    }
  }, null, 2);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Paste your deck JSON here:
        </p>
        <button
          onClick={handleCopyFormat}
          className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
          type="button"
        >
          {copied ? 'Copied!' : 'Copy format'}
        </button>
      </div>
      <textarea
        className="w-full h-24 px-3 py-2 border rounded font-mono text-sm dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 resize-y"
        value={deckJson}
        onChange={(e) => setDeckJson(e.target.value)}
        placeholder={exampleJson}
      />
      
      {error && (
        <div className="mt-2 p-2 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}
      
      <div className="flex justify-end mt-4">
        <button
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded shadow disabled:bg-blue-300 dark:disabled:bg-blue-700/50"
          onClick={handleImport}
          disabled={isImporting || !deckJson.trim()}
        >
          {isImporting ? 'Importing...' : 'Import Deck'}
        </button>
      </div>
    </div>
  );
} 
